import { Box, IconButton, useTheme } from "@mui/material";
import { DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { DocumentVersionBestPracticeResultOverviewViewModel } from "@common/components/bestPracticeCheckDialog/BestPracticeCheckDialog.types.ts";
import { aiMagicDustIcon } from "@common/icons.tsx";
import PlanDialogWrapper from "../../../plan/planDialogWrapper/planDialogWrapper.tsx";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";

export interface BestPracticeCoverageResultProps {
    result?: DocumentVersionBestPracticeResultOverviewViewModel;
    isTooLargeForAi: boolean;
    onClick: () => void;
}

// The AI icon is a hardcoded-gradient SVG, so MUI's disabled coloring cannot dim it.
const disabledIconOpacity = 0.38;

function BestPracticeCoverageResult(props: BestPracticeCoverageResultProps) {
    const theme = useTheme();
    const { translateString } = useTranslation();
    const { addonsFeatures } = usePlanContext();

    const iconButtonStyle = { "& svg": { height: 18, width: 18 } };
    if (!addonsFeatures.aiFeatures) {
        return (
            <IconButton
                sx={{ ...iconButtonStyle, paddingTop: 0.5, paddingBottom: 0.5 }}
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                <PlanDialogWrapper text={translateString("aiAddOnPurchaseTooltip")} enabled hoverText={translateString("aiAddOnPurchaseTooltip")}>
                    {aiMagicDustIcon}
                </PlanDialogWrapper>
            </IconButton>
        );
    }

    if (props.result) {
        const { compliant, partialCompliant, missing } = props.result;
        const total = compliant + partialCompliant + missing;

        if (total === 0) {
            return undefined;
        }

        return (
            <Box
                sx={{ display: "flex", flexWrap: "wrap", columnGap: 3, rowGap: 1 }}
                onClick={(e) => {
                    e.stopPropagation();
                    props.onClick();
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: theme.palette.secondary.main }} />
                    <span style={{ lineHeight: 1, whiteSpace: "nowrap" }}>
                        {compliant} {translateString("fulfilled")}
                    </span>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: theme.customPalette.yellow }} />
                    <span style={{ lineHeight: 1, whiteSpace: "nowrap" }}>
                        {partialCompliant} {translateString("partiallyFulfilled")}
                    </span>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: theme.palette.error.main }} />
                    <span style={{ lineHeight: 1, whiteSpace: "nowrap" }}>
                        {missing} {translateString("missingElements")}
                    </span>
                </Box>
            </Box>
        );
    }

    if (props.isTooLargeForAi) {
        // A disabled button fires no hover events, so the span carries the tooltip and stops the row click.
        return (
            <DotLegalTooltip text={translateString("textExtractionErrorFileTooLarge")}>
                <Box component={"span"} onClick={(e) => e.stopPropagation()}>
                    <IconButton sx={{ ...iconButtonStyle, opacity: disabledIconOpacity }} disabled>
                        {aiMagicDustIcon}
                    </IconButton>
                </Box>
            </DotLegalTooltip>
        );
    }

    return (
        <IconButton
            sx={iconButtonStyle}
            onClick={(e) => {
                e.stopPropagation();
                props.onClick();
            }}
        >
            {aiMagicDustIcon}
        </IconButton>
    );
}

export default BestPracticeCoverageResult;
